import { useState, useEffect } from "react";
import type { Contact, ContactGroup } from "../types";
import { GROUP_COLORS } from "../types";

interface Props {
  open: boolean;
  editTarget: Contact | null;
  groups: ContactGroup[];
  onClose: () => void;
  onSave: (data: Omit<Contact, "id" | "createdAt">) => void;
}

interface FormState {
  name: string;
  phone: string;
  email: string;
  memo: string;
  groupIds: string[];
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}

const DEFAULT_FORM: FormState = { name: "", phone: "", email: "", memo: "", groupIds: [] };

export default function ContactFormModal({ open, editTarget, groups, onClose, onSave }: Props) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    if (open) {
      if (editTarget) {
        setForm({
          name: editTarget.name,
          phone: editTarget.phone,
          email: editTarget.email ?? "",
          memo: editTarget.memo ?? "",
          groupIds: editTarget.groupIds,
        });
      } else {
        setForm(DEFAULT_FORM);
      }
      setErrors({});
    }
  }, [open, editTarget]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) errs.name = "이름을 입력하세요";
    if (!form.phone.trim()) errs.phone = "전화번호를 입력하세요";
    else if (form.phone.replace(/\D/g, "").length < 10) errs.phone = "올바른 전화번호를 입력하세요";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "올바른 이메일 형식이 아닙니다";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      name: form.name.trim(),
      phone: form.phone,
      email: form.email.trim() || undefined,
      memo: form.memo.trim() || undefined,
      groupIds: form.groupIds,
      lastSentAt: editTarget?.lastSentAt,
    });
  };

  const toggleGroup = (gid: string) => {
    setForm((prev) => ({
      ...prev,
      groupIds: prev.groupIds.includes(gid)
        ? prev.groupIds.filter((id) => id !== gid)
        : [...prev.groupIds, gid],
    }));
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div className="relative w-full md:max-w-md bg-[#0d0d1a] border border-white/[0.1] rounded-t-2xl md:rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/30 to-pink-500/30">
              <i className={`${editTarget ? "ri-user-settings-line" : "ri-user-add-line"} text-violet-400 text-base`}></i>
            </div>
            <h2 className="text-white font-semibold text-base">
              {editTarget ? "연락처 수정" : "새 연락처 추가"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors rounded-lg hover:bg-white/[0.06]"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 space-y-4 overflow-y-auto flex-1">
          {/* Name */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">
              이름 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="홍길동"
              maxLength={20}
              className={`w-full bg-white/[0.05] border rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${
                errors.name ? "border-red-500/50 focus:border-red-500/70" : "border-white/[0.1] focus:border-violet-500/60"
              }`}
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">
              전화번호 <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-smartphone-line text-gray-500 text-sm"></i>
              </div>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                placeholder="010-0000-0000"
                className={`w-full bg-white/[0.05] border rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${
                  errors.phone ? "border-red-500/50 focus:border-red-500/70" : "border-white/[0.1] focus:border-violet-500/60"
                }`}
              />
            </div>
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">이메일 (선택)</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-mail-line text-gray-500 text-sm"></i>
              </div>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="example@email.com"
                className={`w-full bg-white/[0.05] border rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${
                  errors.email ? "border-red-500/50 focus:border-red-500/70" : "border-white/[0.1] focus:border-violet-500/60"
                }`}
              />
            </div>
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Groups */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-2">그룹 (선택)</label>
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => {
                const cfg = GROUP_COLORS.find((c) => c.value === g.color) ?? GROUP_COLORS[0];
                const isSelected = form.groupIds.includes(g.id);
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleGroup(g.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
                      isSelected
                        ? `${cfg.bg} ${cfg.text} ${cfg.border}`
                        : "bg-white/[0.04] text-gray-500 border-white/[0.08] hover:text-gray-300"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? cfg.dot : "bg-gray-600"}`}></span>
                    {g.name}
                    {isSelected && <i className="ri-check-line text-xs"></i>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Memo */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">메모 (선택)</label>
            <textarea
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              placeholder="연락처에 대한 메모를 입력하세요"
              rows={3}
              maxLength={200}
              className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 transition-all resize-none"
            />
            <p className="text-gray-600 text-xs mt-1 text-right">{form.memo.length}/200</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.08] flex gap-3">
          <button
            onClick={handleSubmit}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white text-sm font-bold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2"
          >
            <i className={`${editTarget ? "ri-save-line" : "ri-user-add-line"} text-base`}></i>
            {editTarget ? "저장하기" : "추가하기"}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}